import { getFinalDota2GoldAdvantage } from 'app/components/Blocks/NameBlock/MatchScore/utils';

// Count games won by an opponent. Works on any game list carrying `winner_id`
// (aggregated live model or the composed match skeleton).
const countGamesWon = (
  games: { winner_id: number | null }[],
  opponentId: number | undefined
): number => (opponentId ? games.filter(game => game.winner_id === opponentId).length : 0);

// Current game to display: the running one, else the last finished, else the
// first not-started. Mirrors MatchScore's getScoreboardGame for the aggregated model.
const getCurrentAggregatedGame = <T extends Pick<AggregatedGame, 'status' | 'position'>>(
  games: T[]
): T | undefined => {
  const running = games.find(game => game.status === 'running');
  if (running) return running;

  const lastFinished = [...games]
    .sort((a, b) => b.position - a.position)
    .find(game => game.status === 'finished');
  if (lastFinished) return lastFinished;

  return [...games].sort((a, b) => a.position - b.position).find(g => g.status === 'not_started');
};

// Side an opponent plays in the current game of the composed match skeleton (the
// live feed's sides are read in NameBlockGrad, as team1Side); null while unknown.
const getCurrentOpponentSide = (games: MatchGame[], opponentId: number | undefined): Side | null =>
  getCurrentAggregatedGame(games)?.opponents.find(opponent => opponent.id === opponentId)?.side ??
  null;

// Each side's color token in the theme.
const SIDE_COLORS: Record<Side, keyof WidgetTheme & `vg_${string}`> = {
  blue: 'vg_lol_blue',
  red: 'vg_lol_red',
  radiant: 'vg_dota_radiant',
  dire: 'vg_dota_dire',
  'counter-terrorist': 'vg_cs_counterterro',
  terrorist: 'vg_cs_terro'
};

const OPPOSITE_SIDES: Record<Side, Side> = {
  blue: 'red',
  red: 'blue',
  radiant: 'dire',
  dire: 'radiant',
  'counter-terrorist': 'terrorist',
  terrorist: 'counter-terrorist'
};

// The --design_team_<team>_* variables the name block uses, in the side's color. The
// box backgrounds are tints of it; the box text keeps the theme's.
const getSideTeamVariables = (theme: WidgetTheme, team: 1 | 2, side: Side) => {
  const color = theme[SIDE_COLORS[side]];
  return {
    [`--design_team_${team}`]: color,
    [`--design_team_${team}_text_overlay`]: color,
    [`--design_team_${team}_box_background_start`]: `color-mix(in srgb, ${color} 50%, transparent)`,
    [`--design_team_${team}_box_background_end`]: `color-mix(in srgb, ${color} 60%, transparent)`
  };
};

// Team colors following the current sides (design_team_1 is always opponent 1), as
// CSS variables to set on the block. undefined keeps the theme's own team colors:
// side not known yet.
const getSideColorsStyle = (
  theme: WidgetTheme,
  opponent1Side: Side | null
): React.CSSProperties | undefined =>
  opponent1Side
    ? {
        ...getSideTeamVariables(theme, 1, opponent1Side),
        ...getSideTeamVariables(theme, 2, OPPOSITE_SIDES[opponent1Side])
      }
    : undefined;

const hasField = (
  availableFields: string[],
  field: Dota2AvailableField | CsAvailableField
): boolean => availableFields.includes(field);

// cs-go teams come in two shapes and the round score changes key between them:
// the detailed overlay replaces the odds team and emits `score`, while a basic
// (odds-fallback) team keeps `round_score`. `available_fields` advertises the
// same `round_score` token either way, so read through here, never inline.
const getCsRoundScore = (team: AggregatedTeamCsAny | undefined): number => {
  if (!team) return 0;
  return 'score' in team ? team.score : team.round_score;
};

// Players only exist on a detailed team.
const getCsPlayers = (team: AggregatedTeamCsAny | undefined): AggregatedPlayerCs[] =>
  team && 'players' in team ? team.players : [];

// Present on both team shapes — decoupled from `detailed`, so even a
// not-yet-played game carries a map winrate.
const getCsMapWinrate = (team: AggregatedTeamCsAny | undefined): AggregatedMapWinrateCs | null =>
  team?.map_winrate ?? null;

// Round currently being played: every finished round scored for exactly one
// side, so the next round number is the two scores plus one. The CS contract
// carries no round number of its own.
const getCsRoundNumber = (
  team1: AggregatedTeamCsAny | undefined,
  team2: AggregatedTeamCsAny | undefined
): number => getCsRoundScore(team1) + getCsRoundScore(team2) + 1;

export {
  getFinalDota2GoldAdvantage,
  countGamesWon,
  getCurrentAggregatedGame,
  getCurrentOpponentSide,
  getSideColorsStyle,
  hasField,
  getCsRoundScore,
  getCsPlayers,
  getCsMapWinrate,
  getCsRoundNumber
};
